import { Box, SvgIcon, Typography } from '@mui/material';
import { memoize } from 'lodash-es';
import React, { FC, useCallback, useMemo } from 'react';

import SingleChevronRight from '@sbu/assets.svg/src/single-chevron-right.svg';
import StarIcon from '@sbu/assets.svg/src/star.svg';
import { Link } from '@sbu/components.link';
import { IconButton, Tooltip } from '@sumologic/yama-ds-components';

import {
  CreateFavoriteFieldOperationMutation as CreateFavoriteFieldOperation,
  CreateFavoriteFieldOperationMutationVariables as CreateFavoriteFieldOperationVariables,
  DeleteFavoriteFieldOperationMutation as DeleteFavoriteFieldOperation,
  DeleteFavoriteFieldOperationMutationVariables as DeleteFavoriteFieldOperationVariables,
  FavoriteFieldsQueryOperationQuery as FavoriteFieldsQueryOperation,
  FavoriteFieldType,
} from 'core/graphql';
import {
  CREATE_FAVORITE_FIELDS_MUTATION,
  DELETE_FAVORITE_FIELDS_MUTATION,
  FAVORITE_FIELDS_QUERY,
} from 'shared/components/internal/FavoriteField';
import { useMutation, useQuery } from 'shared/hooks';

import { LabeledPropertyType } from '../../shared';

export const spliceProperty = memoize(
  (originalProperties: LabeledPropertyType[], matchKeys: string | string[]) => {
    const properties = [...originalProperties];
    const keys = Array.isArray(matchKeys) ? matchKeys : [matchKeys];
    const matches: (LabeledPropertyType | undefined)[] = [];
    keys.forEach((matchKey) => {
      const index = properties.findIndex(({ name }) => name === matchKey);
      const [match] = index === -1 ? [undefined] : properties.splice(index, 1);
      matches.push(match);
    });

    return { matches, properties };
  }
);

export const useLocationFavoriteFields = (locationFields: Array<string>) => {
  const { data } = useQuery<FavoriteFieldsQueryOperation>(
    FAVORITE_FIELDS_QUERY
  );

  const favoriteFields = data?.favoriteFields?.objects ?? [];

  return favoriteFields.filter((favoriteField) =>
    locationFields.includes(favoriteField?.fieldName)
  );
};

interface LocationData {
  city?: LabeledPropertyType;
  countryName?: LabeledPropertyType;
  latitude?: LabeledPropertyType;
  longitude?: LabeledPropertyType;
  region?: LabeledPropertyType;
  type?: string;
}

interface LocationLabelProps {
  data: LocationData;
  showMapLink?: boolean;
  showIcon?: boolean;
}

// complexity 19
// eslint-disable-next-line complexity
export const LocationLabel: FC<React.PropsWithChildren<LocationLabelProps>> = ({
  data,
  showMapLink = true,
  showIcon = false,
}) => {
  const { city, countryName, latitude, longitude, region, type } = data;

  const locationFields = useMemo(
    () =>
      type
        ? [`${type}_ip_city`, `${type}_ip_countryName`, `${type}_ip_region`]
        : [],
    [type]
  );
  const location = [
    ...(city && city.value !== 'Unassigned' ? [city.value] : []),
    ...(region && region.value !== 'Unassigned' ? [region.value] : []),
    ...(countryName && countryName.value !== 'Unassigned'
      ? [countryName.value]
      : []),
  ].join(', ');
  const latValue = latitude && latitude.value;
  const lonValue = longitude && longitude.value;
  const favoriteFields = useLocationFavoriteFields(locationFields);

  const [createFavoriteField] = useMutation<
    CreateFavoriteFieldOperation,
    CreateFavoriteFieldOperationVariables
  >(CREATE_FAVORITE_FIELDS_MUTATION);

  const [deleteFavoriteField] = useMutation<
    DeleteFavoriteFieldOperation,
    DeleteFavoriteFieldOperationVariables
  >(DELETE_FAVORITE_FIELDS_MUTATION);

  const isLocationFavoriteField = useMemo(
    () => favoriteFields.length > 2,
    [favoriteFields]
  );

  /**
   * Location consists of three fields(countryName, city, region).
   * We are creating new favorite field for each of the three fields associated with location
   * Favorite field api does not support multi creations, so we are making three mutation requests
   * one for each field(countryName, city, region).
   */
  const deleteLocationFavoriteFields = useCallback(async () => {
    await Promise.all(
      favoriteFields.map((favoriteField, index) => {
        let deleteFavoriteFieldParams: any = {
          variables: { id: favoriteField.id },
        };
        if (index + 1 === locationFields.length) {
          deleteFavoriteFieldParams = {
            ...deleteFavoriteFieldParams,
            awaitRefetchQueries: true,
            refetchQueries: [{ query: FAVORITE_FIELDS_QUERY }],
          };
        }
        return deleteFavoriteField(deleteFavoriteFieldParams);
      })
    );
  }, [favoriteFields, locationFields, deleteFavoriteField]);

  /**
   * Location consists of three fields(countryName, city, region).
   * We are deleting all the three fields associated with location
   * Favorite field api does not support multi deletions, so we are making three mutation requests
   * one for each field(countryName, city, region).
   */
  const createLocationFavoriteField = useCallback(async () => {
    await Promise.all(
      locationFields.map((locationFieldName, index) => {
        let createFavoriteFieldParams: any = {
          variables: {
            fieldName: locationFieldName,
            fieldType: FavoriteFieldType.Record,
          },
        };
        if (index + 1 === locationFields.length) {
          createFavoriteFieldParams = {
            ...createFavoriteFieldParams,
            awaitRefetchQueries: true,
            refetchQueries: [{ query: FAVORITE_FIELDS_QUERY }],
          };
        }
        return createFavoriteField(createFavoriteFieldParams);
      })
    );
  }, [locationFields, createFavoriteField]);

  const onClick = useCallback(async () => {
    if (isLocationFavoriteField) {
      deleteLocationFavoriteFields();
    } else {
      createLocationFavoriteField();
    }
  }, [
    isLocationFavoriteField,
    createLocationFavoriteField,
    deleteLocationFavoriteFields,
  ]);

  return (
    <Box alignItems='center' display='flex'>
      {!!location && (
        <Box alignItems='flex-start' display='flex'>
          {showIcon && (
            <Box
              marginRight={0.625}
              sx={{
                opacity: !isLocationFavoriteField ? 0.3 : 1,
              }}
            >
              {
                <Tooltip
                  title={`${
                    isLocationFavoriteField ? 'Disable' : 'Enable'
                  } Favorite Field`}
                >
                  <span>
                    <IconButton
                      size='medium'
                      disabled={false}
                      onClick={onClick}
                      sx={{
                        mx: 0.625,
                        p: 0,
                        color: ({ palette }) => palette.warning.main,
                      }}
                    >
                      <StarIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              }
            </Box>
          )}
          <Typography color='text.secondary' mr={1.25}>
            &#47;&#47; {location}
          </Typography>
        </Box>
      )}
      {showMapLink && !!latValue && !!lonValue && (
        <Link
          variant='a'
          fontSize='0.875rem'
          href={`https://maps.google.com/?q=${latValue},${lonValue}`}
          rel='noopener noreferrer'
          target='_blank'
          title='Open map'
        >
          Open Map{' '}
          <SvgIcon
            component={SingleChevronRight}
            inheritViewBox
            sx={{
              color: ({ palette }) => palette.text.secondary,
              fontSize: '.625rem',
            }}
          />
        </Link>
      )}
    </Box>
  );
};
